import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Trophy, Crown, Printer, Download } from "lucide-react";
import { exportToCSV, handlePrint } from "../../utils/exportUtils";

const AGE_GROUPS = ["mini", "junior", "senior", "adult"];

const YearEndPage = () => {
  const [standings, setStandings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStandings();
  }, []);

  const fetchStandings = async () => {
    try {
      const response = await axios.get(`${API}/results/year-end`);
      setStandings(response.data);
    } catch (error) {
      toast.error("Failed to fetch year-end standings");
    } finally {
      setLoading(false);
    }
  };

  const handleExportYearEnd = () => {
    const exportData = [];
    AGE_GROUPS.forEach(ag => {
      if (standings[ag]) {
        standings[ag].forEach((contestant, idx) => {
          exportData.push({
            age_group: ag.charAt(0).toUpperCase() + ag.slice(1),
            rank: idx + 1,
            contestant: contestant.contestant_name,
            total_points: contestant.total_points
          });
        });
      }
    });
    
    exportToCSV(
      exportData, 
      `year_end_standings_${new Date().getFullYear()}.csv`,
      ["age_group", "rank", "contestant", "total_points"]
    );
    toast.success("Year-end standings exported to CSV");
  };

  const handlePrintYearEnd = () => {
    handlePrint("Year End All-Around Standings", new Date().getFullYear().toString());
  };

  const hasAnyData = Object.values(standings).some(group => group && group.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container" data-testid="yearend-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Year End All-Around</h1>
          <p className="text-text-secondary mt-1">Combined points from all play days by age group</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintYearEnd} disabled={!hasAnyData} data-testid="print-yearend-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleExportYearEnd} disabled={!hasAnyData} data-testid="export-yearend-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1 className="font-heading font-bold text-2xl">TTP Play Day - Year End All-Around Standings</h1>
        <p>{new Date().getFullYear()} Season</p>
      </div>

      {!hasAnyData ? (
        <div className="bg-white rounded-xl border border-primary/20 p-12 text-center">
          <Crown className="h-12 w-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary">No year-end data available yet</p>
          <p className="text-text-muted text-sm mt-1">Points will appear here after play days are completed</p>
        </div>
      ) : (
        <Tabs defaultValue={AGE_GROUPS[0]} className="space-y-4">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0 no-print">
            {AGE_GROUPS.map((ag) => (
              <TabsTrigger 
                key={ag} 
                value={ag}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground bg-white border border-primary/20 capitalize"
              >
                {ag} ({standings[ag]?.length || 0})
              </TabsTrigger>
            ))}
          </TabsList>

          {AGE_GROUPS.map((ag) => (
            <TabsContent key={ag} value={ag} className="print:block">
              <div className="bg-white rounded-xl border border-primary/20 overflow-hidden mb-6">
                <div className="p-4 border-b border-primary/20 bg-secondary/30">
                  <h3 className="font-heading font-bold text-xl text-text-primary capitalize flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-accent" />
                    {ag} Division Standings
                  </h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="font-heading font-bold text-text-primary w-20">Rank</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary">Contestant</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right">Total Points</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(!standings[ag] || standings[ag].length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-text-secondary">
                          No contestants in this division
                        </TableCell>
                      </TableRow>
                    ) : (
                      standings[ag].map((contestant, idx) => (
                        <TableRow 
                          key={contestant.contestant_id} 
                          className={`hover:bg-surface-muted ${idx === 0 ? 'bg-yellow-50' : ''}`}
                          data-testid={`yearend-${contestant.contestant_id}`}
                        >
                          <TableCell className="font-heading font-bold text-lg">
                            <div className="flex items-center gap-2">
                              {idx === 0 && <Crown className="h-5 w-5 text-yellow-500" />}
                              {idx + 1}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-text-primary">{contestant.contestant_name}</TableCell>
                          <TableCell className="text-right">
                            <span className="font-heading font-bold text-xl text-accent">
                              {contestant.total_points}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Print all divisions */}
      <div className="hidden print:block">
        {AGE_GROUPS.map((ag) => (
          standings[ag] && standings[ag].length > 0 && (
            <div key={ag} className="mb-8 page-break-inside-avoid">
              <h3 className="font-heading font-bold text-xl capitalize mb-2">{ag} Division</h3>
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="border p-2 text-left">Rank</th>
                    <th className="border p-2 text-left">Contestant</th>
                    <th className="border p-2 text-right">Total Points</th>
                  </tr>
                </thead>
                <tbody>
                  {standings[ag].map((contestant, idx) => (
                    <tr key={contestant.contestant_id}>
                      <td className="border p-2">{idx + 1}</td>
                      <td className="border p-2">{contestant.contestant_name}</td>
                      <td className="border p-2 text-right font-bold">{contestant.total_points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export default YearEndPage;
